/**
 * Document Service - API functions for document operations
 * Fetches documents from Booqable and sideloads customer and order details
 */
import { apiFetch } from '../lib/apiFetch';

export interface CompanyDetails {
  id: string;
  name: string;
  logoUrl?: string;
  address1?: string;
  address2?: string;
  city?: string;
  region?: string;
  zipcode?: string;
  country?: string;
  phone?: string;
  email?: string;
}

export interface DocumentLine {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface DocumentDetails {
  id: string;
  number: string;
  type: string;
  status: string;
  invoiceDate?: string;
  exclTaxes: number;
  inclTaxes: number;
  deposit?: number;
  due?: number;
  paid?: number;
  paymentStatus?: 'paid' | 'payment_due';
  confirmed?: boolean;

  // Customer details (sideloaded)
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress1?: string;
  customerAddress2?: string;
  customerCity?: string;
  customerRegion?: string;
  customerZipcode?: string;
  customerCountry?: string;

  // Order details (sideloaded)
  orderId?: string;
  orderNumber?: string;
  orderStartDate?: string;
  orderStopDate?: string;
  // Order's persisted tech-fee waiver (orders.waive_tech_fee). Seeds the Send
  // dialog's "Waive technology fee" box so a send doesn't clobber an existing
  // waiver. Undefined when the opener doesn't know it (box defaults unchecked).
  orderWaivesTechFee?: boolean;

  // Line items
  lines?: DocumentLine[];

  // Related documents from the same order
  orderDocuments?: {
    id: string;
    number: string;
    type: string;
    amount: number;
    date: string;
    // Whether the document is finalized. For invoices, false === a draft
    // (pro forma) that must not be emailed to a customer. Non-invoice docs
    // (receipts, POs) leave this undefined — the draft-send guard is
    // invoice-only.
    finalized?: boolean;
  }[];
}

/**
 * Fetch a single document by ID with sideloaded customer and order details
 */
export const fetchDocumentById = async (documentId: string): Promise<DocumentDetails> => {
  // Step 1: Fetch the document with includes for customer, order, and lines
  const documentResponse = await apiFetch(
    `/booqable-proxy`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        method: 'GET',
        path: `/api/4/documents/${documentId}?include=customer,order,lines`,
      }),
    }
  );

  if (!documentResponse.ok) {
    throw new Error(`Failed to fetch document: ${documentResponse.statusText}`);
  }

  const documentData = await documentResponse.json();
  const doc = documentData.data;
  const included = documentData.included || [];

  // Extract customer from included data
  const customer = included.find(
    (item: any) => item.type === 'customers' && item.id === doc.relationships?.customer?.data?.id
  );

  // Extract order from included data
  const order = included.find(
    (item: any) => item.type === 'orders' && item.id === doc.relationships?.order?.data?.id
  );

  // Extract lines from included data
  const lineIds = doc.relationships?.lines?.data?.map((l: any) => l.id) || [];
  const lines = included.filter(
    (item: any) => item.type === 'lines' && lineIds.includes(item.id)
  );

  // Step 2: Fetch customer properties (phone, address) if customer exists
  let customerPhone = '';
  let customerAddress: any = null;

  if (customer) {
    // Fetch customer properties
    const propsResponse = await apiFetch(
      `/booqable-proxy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'GET',
          path: `/api/4/properties?filter[owner_id]=${customer.id}&filter[owner_type]=customers`,
        }),
      }
    );

    if (propsResponse.ok) {
      const propsData = await propsResponse.json();
      const phoneProperty = propsData.data?.find((p: any) => p.attributes.identifier === 'phone');
      if (phoneProperty) {
        customerPhone = phoneProperty.attributes.value || '';
      }
    }

    // Fetch customer address
    const addressResponse = await apiFetch(
      `/booqable-proxy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'GET',
          path: `/api/4/addresses?filter[customer_id]=${customer.id}`,
        }),
      }
    );

    if (addressResponse.ok) {
      const addressData = await addressResponse.json();
      customerAddress = addressData.data?.find((a: any) => a.attributes.primary) || addressData.data?.[0];
    }

    // Fallback: Use address from document attributes if customer address not found
    if (!customerAddress && doc.attributes?.address) {
      // Parse address - split by line breaks
      const addressLines = doc.attributes.address.split(/\r?\n/).filter((line: string) => line.trim());

      customerAddress = {
        attributes: {
          address1: addressLines[0] || '',
          address2: addressLines[1] || '',
          city: addressLines[2] || '',
          region: addressLines[3] || '',
          zipcode: addressLines[4] || '',
          country: addressLines[5] || '',
        }
      };
    }
  }

  // Step 3: Fetch all documents for this order (to show related documents)
  let orderDocuments: any[] = [];

  if (order) {
    const orderDocsResponse = await apiFetch(
      `/booqable-proxy`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'GET',
          path: `/api/4/documents?filter[order_id]=${order.id}`,
        }),
      }
    );

    if (orderDocsResponse.ok) {
      const orderDocsData = await orderDocsResponse.json();
      orderDocuments = (orderDocsData.data || []).map((d: any) => ({
        id: d.id,
        number: d.attributes.prefix_with_number || d.attributes.number || '',
        type: d.attributes.document_type || 'invoice',
        amount: d.attributes.grand_total_in_cents ? d.attributes.grand_total_in_cents / 100 : 0,
        date: d.attributes.date || d.attributes.created_at || '',
        finalized: d.attributes.finalized ?? false,
      }));
    }
  }

  // Map document data
  const attrs = doc.attributes || {};

  return {
    id: doc.id,
    number: attrs.prefix_with_number || attrs.number || '',
    type: attrs.document_type || 'invoice',
    status: attrs.status || '',
    invoiceDate: attrs.date || attrs.created_at || '',
    exclTaxes: attrs.total_in_cents ? attrs.total_in_cents / 100 : 0,
    inclTaxes: attrs.grand_total_in_cents ? attrs.grand_total_in_cents / 100 : 0,
    deposit: attrs.deposit_in_cents ? attrs.deposit_in_cents / 100 : 0,
    due: attrs.to_be_paid_in_cents ? attrs.to_be_paid_in_cents / 100 : 0,
    paid: attrs.paid_in_cents ? attrs.paid_in_cents / 100 : 0,
    paymentStatus: attrs.to_be_paid_in_cents === 0 ? 'paid' : 'payment_due',
    confirmed: attrs.confirmed || false,

    // Customer details
    customerId: customer?.id,
    customerName: customer?.attributes?.name || '',
    customerEmail: customer?.attributes?.email || '',
    customerPhone: customerPhone,
    customerAddress1: customerAddress?.attributes?.address1 || '',
    customerAddress2: customerAddress?.attributes?.address2 || '',
    customerCity: customerAddress?.attributes?.city || '',
    customerRegion: customerAddress?.attributes?.region || '',
    customerZipcode: customerAddress?.attributes?.zipcode || '',
    customerCountry: customerAddress?.attributes?.country || '',

    // Order details
    orderId: order?.id,
    orderNumber: order?.attributes?.number || '',
    orderStartDate: order?.attributes?.starts_at || '',
    orderStopDate: order?.attributes?.stops_at || '',

    // Line items
    lines: lines.map((line: any) => ({
      id: line.id,
      description: line.attributes?.item_name || line.attributes?.title || '',
      quantity: line.attributes?.quantity || 1,
      rate: line.attributes?.price_each_in_cents ? line.attributes.price_each_in_cents / 100 : 0,
      amount: line.attributes?.charge_length_in_cents
        ? line.attributes.charge_length_in_cents / 100
        : (line.attributes?.price_each_in_cents && line.attributes?.quantity
          ? (line.attributes.price_each_in_cents * line.attributes.quantity) / 100
          : 0),
    })),

    // Related documents
    orderDocuments,
  };
};

// Booqable caps a page at 500. We page through ALL pages so the documents list
// stops silently truncating past 500 rows.
const DOCUMENT_PAGE_SIZE = 500;
const MAX_DOCUMENT_PAGES = 40;

const fetchDocumentPage = async (page: number): Promise<{ data: any[]; included: any[]; meta: any }> => {
  const response = await apiFetch(
    `/booqable-proxy`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        method: 'GET',
        path: `/api/4/documents?page[number]=${page}&page[size]=${DOCUMENT_PAGE_SIZE}&include=customer,order`,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch documents (page ${page}): ${response.statusText}`);
  }

  const data = await response.json();
  return { data: data.data || [], included: data.included || [], meta: data.meta };
};

/**
 * Resolve total pages from Booqable's `meta`, falling back to "stop on a short
 * page" when meta is absent (documents aren't env-filtered, so a short page
 * reliably signals the last one here).
 */
const resolveDocumentPages = (meta: any, firstPageCount: number): number => {
  if (meta && typeof meta.total_pages === 'number' && meta.total_pages > 0) {
    return Math.min(meta.total_pages, MAX_DOCUMENT_PAGES);
  }
  if (meta && typeof meta.total_count === 'number' && meta.total_count > 0) {
    return Math.min(Math.ceil(meta.total_count / DOCUMENT_PAGE_SIZE), MAX_DOCUMENT_PAGES);
  }
  return firstPageCount < DOCUMENT_PAGE_SIZE ? 1 : MAX_DOCUMENT_PAGES;
};

/**
 * Fetch all documents, paging through every page.
 */
export const fetchDocuments = async (): Promise<DocumentDetails[]> => {
  const first = await fetchDocumentPage(1);
  const documents = [...first.data];
  const included = [...first.included];

  const totalPages = resolveDocumentPages(first.meta, first.data.length);
  for (let page = 2; page <= totalPages; page++) {
    const next = await fetchDocumentPage(page);
    if (next.data.length === 0) break; // defensive: short-circuit if a page is empty
    documents.push(...next.data);
    included.push(...next.included);
  }

  return documents.map((doc: any) => {
    const attrs = doc.attributes || {};

    // Find customer from included data
    const customer = included.find(
      (item: any) => item.type === 'customers' && item.id === doc.relationships?.customer?.data?.id
    );

    // Find order from included data
    const order = included.find(
      (item: any) => item.type === 'orders' && item.id === doc.relationships?.order?.data?.id
    );

    return {
      id: doc.id,
      number: attrs.prefix_with_number || attrs.number || '',
      type: attrs.document_type || 'invoice',
      status: attrs.status || '',
      invoiceDate: attrs.date || attrs.created_at || '',
      exclTaxes: attrs.total_in_cents ? attrs.total_in_cents / 100 : 0,
      inclTaxes: attrs.grand_total_in_cents ? attrs.grand_total_in_cents / 100 : 0,
      deposit: attrs.deposit_in_cents ? attrs.deposit_in_cents / 100 : 0,
      due: attrs.to_be_paid_in_cents ? attrs.to_be_paid_in_cents / 100 : 0,
      paid: attrs.paid_in_cents ? attrs.paid_in_cents / 100 : 0,
      paymentStatus: attrs.to_be_paid_in_cents === 0 ? 'paid' : 'payment_due',
      confirmed: attrs.confirmed || false,

      customerId: customer?.id,
      customerName: customer?.attributes?.name || '',
      customerEmail: customer?.attributes?.email || '',

      orderId: order?.id,
      orderNumber: order?.attributes?.number || '',
    };
  });
};

/**
 * Archive (soft delete) a document.
 *
 * Booqable v4 archives a document via DELETE /api/4/documents/{id} — `archived`
 * is a read-only attribute, so a PUT with `attributes: { archived: true }` is
 * rejected (which silently broke the Delete button on the document preview).
 * For invoices this requires the `delete_invoices` permission. Pro forma
 * invoices may be regenerated by Booqable if the order later changes — that's
 * expected, separate behavior.
 */
export const archiveDocument = async (documentId: string): Promise<void> => {
  const response = await apiFetch(
    `/booqable-proxy`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        method: 'DELETE',
        path: `/api/4/documents/${documentId}`,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to archive document: ${response.statusText}`);
  }

  console.log('✓ Document archived:', documentId);
};

/**
 * Fetch company details including logo
 */
export const fetchCompanyDetails = async (): Promise<CompanyDetails> => {
  const response = await apiFetch(
    `/booqable-proxy`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        method: 'GET',
        path: '/api/4/companies/current',
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch company details: ${response.statusText}`);
  }

  const data = await response.json();
  const company = data.data;
  const attrs = company.attributes || {};

  return {
    id: company.id,
    name: attrs.name || '',
    logoUrl: attrs.logo_url || '',
    address1: attrs.address_line_1 || '',
    address2: attrs.address_line_2 || '',
    city: attrs.city || '',
    region: attrs.region || '',
    zipcode: attrs.zipcode || '',
    country: attrs.country || '',
    phone: attrs.phone || '',
    email: attrs.email || '',
  };
};
